import { createDisposers } from '../../core/disposers.js';

export class TagEventLinker {
    #mediator;
    #docId;
    #bag = createDisposers();

    constructor(mediator, docId = null) {
        this.#mediator = mediator;
        this.#docId = docId;
    }

    registerEvents(callbacks) {
        this.#subscribeToStoreEvents(callbacks);
        this.#subscribeToUserActions(callbacks);
        this.#subscribeToDomInteractions(callbacks);
        this.#subscribeToMountEvents(callbacks);
        this.#subscribeToLegacyUiEvents(callbacks);
    }

    unregisterEvents() {
        this.#bag.dispose();
    }

    // Subscribe to an event of THIS document only — every event carries the docId of the popup it belongs to. One
    // place for the rule, so a new subscription cannot forget it (cleanup phase C, plan
    // bugfix_2026-10-06_permission-tagging).
    #onDoc(event, handler) {
        return this.#mediator.subscribe(event, (payload) => {
            if (payload.docId !== this.#docId) return;
            handler(payload);
        });
    }

    #subscribeToStoreEvents(callbacks) {
        this.#bag.add(
            this.#onDoc('tag:created',       ({ tagId, model })              => callbacks.onTagCreated?.(tagId, model)),
            this.#onDoc('tag:updated',       ({ tagId, patch, prev, model }) => callbacks.onTagUpdated?.(tagId, patch, prev, model)),
            this.#onDoc('tag:removed',       ({ tagId, model })              => callbacks.onTagRemoved?.(tagId, model)),
            this.#onDoc('tag:store:cleared', ()                              => callbacks.onStoreCleared?.())
        );
    }

    // Phase 3: user-action events published by TagBoxDomAdapter
    #subscribeToUserActions(callbacks) {
        this.#bag.add(
            this.#onDoc('tag:user:draw:complete',  ({ dbid, data, pix })                => callbacks.onUserDraw?.(dbid, data, pix)),
            this.#onDoc('tag:user:delete:request', ({ tagId, model })                   => callbacks.onUserDelete?.(tagId, model)),
            this.#onDoc('tag:user:text:changed',   ({ tagId, newText, newLevel, prev }) => callbacks.onUserTextChange?.(tagId, newText, newLevel, prev))
        );
    }

    // M1a: the module's own tag:dom:* interaction policy (commit-on-interaction, hide-on-draw-start, text-change
    // dedup) — moved out of documents.js. docId-scoped, torn down with #bag like the rest.
    #subscribeToDomInteractions(callbacks) {
        this.#bag.add(
            this.#onDoc('tag:dom:input:focus',      ({ tagId })       => callbacks.onDomCommitExcept?.(tagId)),
            this.#onDoc('tag:dom:content:changing', ({ tagId })       => callbacks.onDomCommitExcept?.(tagId)),
            this.#onDoc('tag:dom:drag:start',       ({ activeTagId }) => callbacks.onDomCommitExcept?.(activeTagId)),
            this.#onDoc('tag:dom:resize:start',     ({ activeTagId }) => callbacks.onDomCommitExcept?.(activeTagId)),
            this.#onDoc('tag:draw:start',           ()                => callbacks.onDrawStart?.()),
            // A click on the background (no box, no draw) leaves the box being edited (phase speichern, stage 1b).
            this.#onDoc('tag:layer:click',          ()                => callbacks.onLayerClick?.()),
            this.#onDoc('tag:dom:text:changed',     ({ tagId, newText, newLevel }) => callbacks.onDomTextChanged?.(tagId, newText, newLevel))
        );
    }

    // M1b: mount-dependent subs — drag/resize:stop re-mount the box; draw:complete mounts a freshly drawn box.
    // Moved out of documents.js; docId-scoped, torn down with #bag.
    #subscribeToMountEvents(callbacks) {
        this.#bag.add(
            this.#onDoc('tag:dom:drag:stop',   ({ tagId }) => callbacks.onDomDragOrResizeStop?.(tagId)),
            this.#onDoc('tag:dom:resize:stop', ({ tagId }) => callbacks.onDomDragOrResizeStop?.(tagId)),
            this.#onDoc('tag:draw:complete',   ({ pix })   => callbacks.onDrawComplete?.(pix))
        );
    }

    // M1c: subscriptions whose UI action stays in documents.js (injected callbacks): delete-confirm dialog +
    // video time seek. docId-scoped, torn down with #bag.
    #subscribeToLegacyUiEvents(callbacks) {
        this.#bag.add(
            this.#onDoc('tag:dom:close:click',  ({ tagId })            => callbacks.onDomCloseClick?.(tagId)),
            this.#onDoc('tag:dom:time:changed', ({ tagId, startTime }) => callbacks.onDomTimeChanged?.(tagId, startTime)),
            // Escape in the active box (stage 5) — the discard/reset dialog is a legacy service (confirmRevertTag).
            this.#onDoc('tag:dom:revert:request', ({ tagId })          => callbacks.onRevertRequest?.(tagId))
        );
    }
}
